import type { EntityTheme } from '@getaccept/lib-shared/src/branding/types/theme';
import type { HexColor } from '@getaccept/fried-tofu';
import {
  darkenColor,
  getAccessibleTextColor,
  lightenColor,
  getLuminance,
} from '@getaccept/lib-shared/src/branding/helpers/color-utils.helper';
import { hexToRgb } from '@getaccept/lib-shared/src/helpers';
import type { SectionColors } from '../types';
import { CssVariables } from '../..';

export const mapEntityThemeToSectionColors = (entityTheme: EntityTheme): SectionColors => {
  const root = document.documentElement;
  const styles = getComputedStyle(root);
  const baseBlue = styles.getPropertyValue('--base-blue').trim() as HexColor;
  const gray96 = styles.getPropertyValue('--gray-96').trim() as HexColor;
  const gray98 = styles.getPropertyValue('--gray-98').trim() as HexColor;

  return {
    backgroundColor: '#FFFFFF', // Default to white
    h1Color: entityTheme.h1Color,
    h2Color: entityTheme.h2Color,
    textColor: entityTheme.textColor,
    supplementaryColor: entityTheme.supplementaryColor,
    pricingTable: {
      headerBackgroundColor: gray96,
      headerTextColor: baseBlue,
    },
    inputField: {
      backgroundColor: gray98,
      textColor: baseBlue,
    },
  };
};

export const getHexFromVariable = (variable: string) => {
  const root = document.documentElement;
  const styles = getComputedStyle(root);
  return styles.getPropertyValue(variable).trim() as HexColor;
};

export const mergeSectionBrandingWithEntityTheme = (
  entityTheme: EntityTheme,
  sectionBranding?: Partial<SectionColors>
): SectionColors => {
  const entityThemeColors = mapEntityThemeToSectionColors(entityTheme);
  if (!sectionBranding) {
    return entityThemeColors;
  }
  return {
    backgroundColor: sectionBranding.backgroundColor ?? entityThemeColors.backgroundColor,
    h1Color: sectionBranding.h1Color ?? entityThemeColors.h1Color,
    h2Color: sectionBranding.h2Color ?? entityThemeColors.h2Color,
    textColor: sectionBranding.textColor ?? entityThemeColors.textColor,
    supplementaryColor: sectionBranding.supplementaryColor ?? entityThemeColors.supplementaryColor,
    pricingTable: {
      headerBackgroundColor:
        sectionBranding.pricingTable?.headerBackgroundColor ??
        entityThemeColors.pricingTable.headerBackgroundColor,
      headerTextColor:
        sectionBranding.pricingTable?.headerTextColor ??
        entityThemeColors.pricingTable.headerTextColor,
    },
    inputField: {
      backgroundColor:
        sectionBranding.inputField?.backgroundColor ?? entityThemeColors.inputField.backgroundColor,
      textColor: sectionBranding.inputField?.textColor ?? entityThemeColors.inputField.textColor,
    },
    contentBrandingId: sectionBranding.contentBrandingId,
  };
};

export function getBackgroundHoverColor(backgroundColor: HexColor) {
  const [r, g, b] = hexToRgb(backgroundColor);
  const bgLuminance = getLuminance(r, g, b);
  return bgLuminance > 0.2
    ? darkenColor(backgroundColor, 12.5)
    : lightenColor(backgroundColor, 12.5);
}

export const setBackgroundHoverColor = (
  element: HTMLElement,
  backgroundColor: HexColor | 'transparent',
  cssVariable: CssVariables
): void => {
  if (
    backgroundColor === 'transparent' ||
    backgroundColor === '#FFFFFF' ||
    backgroundColor === getHexFromVariable('--gray-96')
  ) {
    element.style.removeProperty(cssVariable);
    return;
  }
  const backgroundHoverColor = getBackgroundHoverColor(backgroundColor);
  element.style.setProperty(cssVariable, backgroundHoverColor);
};

export const setBrandingColors = (sectionContainer: HTMLElement, brandingColors: SectionColors) => {
  sectionContainer.style.setProperty(CssVariables.backgroundColor, brandingColors.backgroundColor);
  sectionContainer.style.setProperty(CssVariables.h1Color, brandingColors.h1Color);
  sectionContainer.style.setProperty(CssVariables.h2Color, brandingColors.h2Color);
  sectionContainer.style.setProperty(CssVariables.textColor, brandingColors.textColor);
  sectionContainer.style.setProperty(
    CssVariables.supplementaryColor,
    brandingColors.supplementaryColor
  );
  sectionContainer.style.setProperty(
    CssVariables.pricingTableHeaderBackgroundColor,
    brandingColors.pricingTable.headerBackgroundColor
  );
  sectionContainer.style.setProperty(
    CssVariables.pricingTableHeaderTextColor,
    brandingColors.pricingTable.headerTextColor
  );
  sectionContainer.style.setProperty(
    CssVariables.inputFieldBackgroundColor,
    brandingColors.inputField.backgroundColor
  );
  sectionContainer.style.setProperty(
    CssVariables.inputFieldTextColor,
    brandingColors.inputField.textColor
  );
  sectionContainer.style.setProperty(
    CssVariables.inputFieldPlaceholderTextColor,
    getHexFromVariable('--text-gray')
  );
  setBackgroundHoverColor(
    sectionContainer,
    brandingColors.backgroundColor,
    CssVariables.pricingTableRowHoverColor
  );
  setBackgroundHoverColor(
    sectionContainer,
    brandingColors.pricingTable.headerBackgroundColor,
    CssVariables.pricingTableBarHoverColor
  );
};

export const hasChangesFromTheme = (formData: SectionColors, theme: EntityTheme): boolean => {
  if (!formData || !theme) return true;

  const themeColors = mapEntityThemeToSectionColors(theme);

  return (
    formData.backgroundColor !== themeColors.backgroundColor ||
    formData.h1Color !== themeColors.h1Color ||
    formData.h2Color !== themeColors.h2Color ||
    formData.textColor !== themeColors.textColor ||
    formData.supplementaryColor !== themeColors.supplementaryColor ||
    formData.pricingTable?.headerBackgroundColor !==
      themeColors.pricingTable?.headerBackgroundColor ||
    formData.pricingTable?.headerTextColor !== themeColors.pricingTable?.headerTextColor ||
    formData.inputField?.backgroundColor !== themeColors.inputField?.backgroundColor ||
    formData.inputField?.textColor !== themeColors.inputField?.textColor
  );
};

export const setInputFieldPlaceholderTextColor = (
  element: HTMLElement,
  backgroundColor: HexColor
): void => {
  if (backgroundColor === getHexFromVariable('--gray-98')) {
    element.style.removeProperty(CssVariables.inputFieldPlaceholderTextColor);
    return;
  }
  const placeholderTextColor = getAccessibleTextColor(backgroundColor);
  element.style.setProperty(CssVariables.inputFieldPlaceholderTextColor, placeholderTextColor);
};
